import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import { useAuth } from './AuthContext';
import { PRODUCT_DETAILS } from './config/productDetails';
import { PRODUCT_PAGES } from './productPages';
import { productMedia } from './config/productMedia';
import { productVisualPlan } from './config/productVisualPlans';
import { completeThreePanelBlock } from './config/productPanelAdditions';
import CyberTechDetailPage from './CyberTechDetailPage';
import VeridianMoreInfoPage from './VeridianMoreInfoPage';
import { MoreInfoHeading, MoreInfoPicture } from './components/moreInfo/CyberTechMoreInfoPrimitives';
import './CyberTechDetailPage.css';
import {
  SERVICE_COMMAND_CENTRE_ENTITLEMENT,
  SCC_CHECKOUT_SEAM_READY,
  isEntitlementActive,
  startPaidProductCheckout,
} from './UtilitiesPage';
import './ProductDetailPage.css';

const cx = (...names) => names.filter(Boolean).join(' ');

function PrimaryAction({ product }) {
  const auth = useAuth();
  const user = auth ? auth.user : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const action = product.action || { kind: 'disabled', label: 'Unavailable' };

  if (action.kind === 'scc') {
    if (isEntitlementActive(user, SERVICE_COMMAND_CENTRE_ENTITLEMENT)) return null;
    const enabled = SCC_CHECKOUT_SEAM_READY && !busy;
    return <div>
      <button className="pd-action" type="button" disabled={!enabled} onClick={enabled ? () => {
        setError('');
        setBusy(true);
        startPaidProductCheckout('scc', { onError: setError, onSettled: () => setBusy(false) });
      } : undefined}>{busy ? 'Starting checkout...' : 'Activate Service Command Centre'}</button>
      {error ? <div className="pd-action-error">{error}</div> : null}
    </div>;
  }

  if (action.kind === 'mailto') {
    const href = 'mailto:' + action.email + '?subject=' + encodeURIComponent(action.subject) + '&body=' + encodeURIComponent(action.body);
    return <a className="pd-action" href={href}>{action.label}</a>;
  }

  return <button className="pd-action" type="button" disabled>{action.label}{action.pill ? ' · ' + action.pill : ''}</button>;
}

function List({ items, cols }) {
  return <ul className="pd-list" style={{ '--cols': cols || 3 }}>{items.map((item, i) => <li key={i}>{item}</li>)}</ul>;
}

function Block({ block }) {
  if (block.t === 'para') return <div className="pd-prose">{block.items.map((item, i) => <p key={i}>{item}</p>)}</div>;
  if (block.t === 'statement') return <div className="pd-statement"><h3>{block.headline}</h3><div className="pd-prose">{block.items.map((item, i) => <p key={i}>{item}</p>)}</div></div>;
  if (block.t === 'chips') return <div className="pd-chips">{block.items.map((item, i) => <span className="pd-chip" key={i}>{item}</span>)}</div>;
  if (block.t === 'list') return <List items={block.items} cols={block.cols} />;
  if (block.t === 'stats') return <div className="pd-stats" style={{ '--cols': block.cols || 4 }}>{block.items.map((item, i) => <div className="pd-stat" key={i}><strong>{item.v}</strong><span>{item.l}</span>{item.sub ? <p>{item.sub}</p> : null}</div>)}</div>;
  if (block.t === 'steps') return <ol className="pd-steps">{block.items.map((item, i) => <li key={i}><b>{item.n}</b><div><h3>{item.title}</h3><p>{item.text}</p></div></li>)}</ol>;
  if (block.t === 'facts') return <div className="pd-grid pd-grid--facts" style={{ '--cols': block.cols || 3 }}>{block.items.map((item, i) => <article className="pd-card" key={i}><small>{item.label}</small><h3>{item.value}</h3></article>)}</div>;
  if (block.t === 'note') return <div className="pd-note">{block.title ? <strong>{block.title}</strong> : null}{block.text}</div>;
  if (block.t === 'callout') return <div className="pd-callout">{block.eyebrow ? <div className="pd-eyebrow">{block.eyebrow}</div> : null}<h3>{block.title}</h3><p>{block.text}</p>{block.items ? <List items={block.items} cols={block.cols || 3} /> : null}</div>;

  const items = block.items || [];
  return <div className={cx('pd-grid', block.t === 'cards' && 'pd-grid--cards')} style={{ '--cols': block.cols || 3 }}>{items.map((item, i) => <article className="pd-card" key={i}>
    {item.sub || item.eyebrow ? <small>{item.sub || item.eyebrow}</small> : null}
    <h3>{item.title || item.label || item.name}</h3>
    {item.price ? <p><strong>{item.price}</strong> {item.billing}</p> : null}
    {item.text ? <p>{item.text}</p> : null}
    {item.items ? <List items={item.items} cols={1} /> : null}
  </article>)}</div>;
}

function ProductVisual({ product, visual, index }) {
  if (visual.type === 'image') return <MoreInfoPicture
    src={visual.src}
    alt={visual.alt}
    caption={`${visual.label}. ${visual.title}`}
    position={visual.position}
    fit={visual.fit}
    className={`pd-cyber-picture${visual.fit === 'contain' ? ' pd-cyber-picture--contain' : ''}`}
  />;

  const items = visual.items || [];
  return <figure className={`pd-media pd-media--cinematic pd-media--${visual.type || 'interface'}`} aria-label={`${product.name}: ${visual.title}`}>
    {visual.src ? <img className="pd-media__backdrop" src={visual.src} alt="" aria-hidden="true" loading="lazy" /> : null}
    <div className="pd-media__shade" />
    <div className="pd-media__content">
      <div className="pd-media__top"><span>{visual.label || 'Product-specific capability evidence'}</span><b>{String(index + 1).padStart(2, '0')}</b></div>
      <h3>{visual.title}</h3>
      <div className="pd-media__stage">
        {items.map((item, itemIndex) => <div className="pd-media__item" key={item}>
          <i>{String(itemIndex + 1).padStart(2, '0')}</i><strong>{item}</strong>
        </div>)}
      </div>
    </div>
  </figure>;
}

function distributeMoments(moments, sectionCount) {
  const buckets = Array.from({ length: sectionCount }, () => []);
  moments.forEach((moment, index) => buckets[Math.min(sectionCount - 1, Math.floor(index * sectionCount / moments.length))].push({ ...moment, visualIndex: index }));
  return buckets;
}

function DetailSection({ product, section, index, visuals }) {
  const dark = index % 3 === 1;
  const blocks = section.blocks.map((block) => completeThreePanelBlock(product.key, section.id, block));
  const wideBlocks = blocks.filter((block) => ['cards', 'facts'].includes(block.t));
  const narrativeBlocks = blocks.filter((block) => !['cards', 'facts'].includes(block.t));
  return <section id={section.id} className={cx('pd-section', dark && 'pd-section--dark')}>
    <div className="pd-shell">
      <MoreInfoHeading eyebrow={section.eyebrow} title={section.title} light={dark}>{section.lead}</MoreInfoHeading>
      {(narrativeBlocks.length || visuals[0]) ? <div className={cx('pd-section__composition', visuals.length && 'has-media', !narrativeBlocks.length && 'media-only', index % 2 && 'is-reverse')}>
        {narrativeBlocks.length ? <div className="pd-body">{narrativeBlocks.map((block, i) => <Block block={block} key={`${section.id}-narrative-${i}`} />)}</div> : null}
        {visuals[0] ? <ProductVisual product={product} visual={visuals[0]} index={visuals[0].visualIndex} /> : null}
      </div> : null}
      {wideBlocks.map((block, i) => <div className="pd-wide-block" key={`${section.id}-wide-${i}`}><Block block={block} /></div>)}
      {visuals.slice(1).map((visual) => <ProductVisual product={product} visual={visual} index={visual.visualIndex} key={`${section.id}-${visual.visualIndex}`} />)}
    </div>
  </section>;
}

function NotFound() {
  const navigate = useNavigate();
  return <div className="pd-page"><ProductNav /><div className="pd-shell" style={{ padding: '80px 0' }}><h1>Product not found</h1><button className="pd-back" onClick={() => navigate('/dashboard')}>Back to Dashboard</button></div></div>;
}

export default function ProductDetailExperience() {
  const { productKey } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth ? auth.user : null;
  const product = PRODUCT_DETAILS[productKey] || null;
  const media = productMedia(productKey);
  const visualPlan = productVisualPlan(productKey);

  useEffect(() => { window.scrollTo(0, 0); }, [productKey]);
  if (productKey === 'cybertech') return <CyberTechDetailPage />;
  if (productKey === 'veridian') return <VeridianMoreInfoPage />;
  // An explicitly registered composition is the route authority.  Product copy may also exist in
  // PRODUCT_DETAILS for cards, pricing and search, but that must never shadow the bespoke page.
  if (PRODUCT_PAGES[productKey]) {
    const BespokePage = PRODUCT_PAGES[productKey];
    return <BespokePage />;
  }
  if (!product) return <NotFound />;

  let status = product.status;
  if (product.action && product.action.kind === 'scc') status = isEntitlementActive(user, SERVICE_COMMAND_CENTRE_ENTITLEMENT)
    ? { label: 'Active on your account', tone: '#34d399' }
    : { label: 'Subscription required', tone: '#fbbf24' };

  const style = {
    '--pd-accent': product.accent,
    '--pd-soft': product.accent + '24',
    '--status': status ? status.tone : '#7dd3fc',
    '--pd-image-position': media && media.position ? media.position : 'center',
  };
  const visualBuckets = distributeMoments(visualPlan?.moments || [], product.sections.length);

  return <div className="pd-page" style={style}>
    <ProductNav />
    <header className={cx('pd-hero', visualPlan?.hero && 'pd-hero--planned')}>
      <div className="pd-hero__art" />
      {visualPlan?.hero ? <img className={cx('pd-hero__image', `pd-hero__image--${visualPlan.hero.mode || 'scene'}`)} src={visualPlan.hero.src} alt={visualPlan.hero.alt} />
        : media && media.placement === 'overlay' ? <img className="pd-hero__image" src={media.src} alt="" aria-hidden="true" /> : null}
      <div className="pd-hero__shade" />
      <div className="pd-hero__content"><div className="pd-shell">
        <button className="pd-back" type="button" onClick={() => navigate(product.backTo.route)}>← {product.backTo.label}</button>
        <div className="pd-kicker">{product.category} · {product.eyebrow}</div>
        <h1>{product.shortName || product.name}</h1>
        <p className="pd-tagline">{product.tagline}</p>
        <p className="pd-purpose">{product.purpose}</p>
        <div className="pd-actions"><PrimaryAction product={product} />{status ? <span className="pd-status">{status.label}</span> : null}<span className="pd-price"><strong>{product.price.main}</strong><span>{product.price.billing}</span></span></div>
        {product.accessNote ? <p className="pd-access">{product.accessNote}</p> : null}
        <div className="pd-facts">{product.heroFacts.map((fact, i) => <div className="pd-fact" key={i}><strong>{fact.v}</strong><span>{fact.l}</span></div>)}</div>
        <div className="pd-chips pd-hero__highlights">{product.highlights.map((item, i) => <span className="pd-chip" key={i}>{item}</span>)}</div>
      </div></div>
    </header>
    <nav className="pd-nav" aria-label={`${product.name} page sections`}><div className="pd-shell">{product.sections.map(section => <a key={section.id} href={`#${section.id}`}>{section.eyebrow || section.title}</a>)}</div></nav>
    {media && media.placement === 'feature' ? <section className="pd-feature"><div className="pd-shell"><figure><img src={media.src} alt={media.alt} width="1536" height="1024" /><figcaption>{media.caption}</figcaption></figure></div></section> : null}
    <main>{product.sections.map((section, index) => <DetailSection product={product} section={section} index={index} visuals={visualBuckets[index] || []} key={section.id} />)}</main>
    <footer className="pd-footer"><div className="pd-shell pd-footer__grid"><div><div className="pd-eyebrow">{product.category}</div><h2>{product.closing ? product.closing.title : product.name}</h2>{product.closing ? <p>{product.closing.text}</p> : null}</div><div className="pd-footer__actions"><PrimaryAction product={product} /><button className="pd-back" type="button" onClick={() => navigate(product.backTo.route)}>← {product.backTo.label}</button></div></div></footer>
  </div>;
}
